/* eslint-disable mindtrip/require-get-server-side-props */
import "@/styles/globals.css"

import { AppProps as NextAppProps } from "next/app"
import EmbeddedApp from "@/apps/embedded-app"
import MinimalApp from "@/apps/minimal-app"
import WebApp from "@/apps/web-app"
import {
  BasicPageProps,
  PageLayout,
  PublicPageProps,
  RefreshNeededProps,
} from "@/types/page"
import { Honeybadger } from "@honeybadger-io/react"

import "../../honeybadger.browser.config"

import { env } from "@/lib/env"
import { setupEnhancedPromise } from "@/lib/errors/enhanced-promise"
import { reportUnhandledRejection } from "@/lib/errors/unhandled-rejection"
import { releaseInputGuard } from "@/lib/input-guard"
import { isClientSide } from "@/lib/is-client-side"
import { RELEASE_VERSION } from "@/lib/version"
import AuthRefresh from "@/components/auth-refresh"

let isAppInit = false

type EmbeddedAppProps = NextAppProps<
  (BasicPageProps & { appType: "embedded" }) | RefreshNeededProps
>
type EmptyAppProps = NextAppProps<
  (PublicPageProps & { appType: "empty" }) | RefreshNeededProps
>
type MinimalAppProps = NextAppProps<
  (PublicPageProps & { appType: "minimal" }) | RefreshNeededProps
>
type WebAppProps = NextAppProps<
  | (PublicPageProps & ({ appType: "web" } | { appType: never }))
  | RefreshNeededProps
>

type AppProps = (
  | WebAppProps
  | EmbeddedAppProps
  | EmptyAppProps
  | MinimalAppProps
) & { Component: { getLayout?: PageLayout } }

// Type predicate to narrow RefreshNeededProps
function isRefreshNeeded(
  pageProps: AppProps["pageProps"]
): pageProps is RefreshNeededProps {
  return "needsRefresh" in pageProps && pageProps.needsRefresh === true
}

// Keep this file light: add app-wide behavior to the WebApp, EmbeddedApp or
// MinimalApp components instead.
export default function App({ Component, pageProps }: AppProps) {
  // React's handlers are attached once it renders, so fields in server HTML
  // can take typing again (see lib/input-guard).
  if (isClientSide()) releaseInputGuard()

  // Check for refresh needed first before accessing appType
  if (isRefreshNeeded(pageProps)) {
    return <AuthRefresh />
  }

  // TypeScript now knows pageProps is not RefreshNeededProps
  const { appType = "web", ...restPageProps } = pageProps
  const honeybadgerEnvironmentSuffix =
    appType === "embedded" ? "-embedded-browser" : "-browser"

  if (isClientSide() && !isAppInit) {
    isAppInit = true
    setupEnhancedPromise()

    // configure Honeybadger in client with runtime env vars
    Honeybadger.configure({
      apiKey: env("NEXT_PUBLIC_HONEYBADGER_API_KEY"),
      environment:
        (env("NEXT_PUBLIC_HONEYBADGER_ENV") ?? "local") +
        honeybadgerEnvironmentSuffix,
      revision: RELEASE_VERSION,
      enableUncaught: true,
      // The SDK's own rejection reporting is off because the listener below
      // replaces it: the SDK only sees the rejection value, while our
      // listener also reads the creation evidence from the promise object.
      enableUnhandledRejection: false,
      // Caps notices per page load so a single tab stuck in an error loop
      // can't burn the notice quota. Counter resets on full page load only,
      // not SPA navigation (Honeybadger.resetMaxErrors() exists if long
      // sessions ever go quiet).
      maxErrors: 20,
      debug: false,
      // reportData: true, // Uncomment if you want to hit HB in development
    })

    window.addEventListener("unhandledrejection", (event) => {
      reportUnhandledRejection(event)
    })

    Honeybadger.logger.debug("Honeybadger configured for browser")
  }

  const getLayout = Component.getLayout ?? ((page) => page)

  switch (appType) {
    case "embedded":
      return (
        <EmbeddedApp {...restPageProps}>
          <Component {...restPageProps} />
        </EmbeddedApp>
      )
    case "empty":
      return <Component {...restPageProps} />
    case "minimal":
      return (
        <MinimalApp {...restPageProps}>
          <Component {...restPageProps} />
        </MinimalApp>
      )
    default:
      return (
        <WebApp {...restPageProps}>
          {getLayout(<Component {...restPageProps} />)}
        </WebApp>
      )
  }
}
